import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, GripVertical, Trash2, Upload } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useChannels } from "@/components/ChannelGrid";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { Stat, MiniPlay, fieldCls } from "@/components/dashboard/Shared";
import { useMe } from "@/context/MeContext";

const usd = (n) => `USD ${Number(n || 0).toFixed(2)}`;

export function AdminStats() {
  const api = useApi();
  const [s, setS] = useState(null);
  useEffect(() => { api("get", "/admin/stats").then(setS).catch(() => {}); }, [api]);
  if (!s) return null;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat id="sales" label="Ventas de créditos" value={usd(s.sales_usd)} accent />
        <Stat id="app-revenue" label="Ingresos de la app" value={usd(s.app_revenue_usd)} />
        <Stat id="commission" label="Comisión envíos (30%)" value={usd(s.commission_usd)} />
        <Stat id="featured" label="Destacadas (100%)" value={usd(s.featured_usd)} />
        <Stat id="payouts" label="Saldo de medios (70%)" value={usd(s.media_payouts_usd)} />
        <Stat id="escrow" label="En espera de respuesta" value={usd(s.escrow_usd)} />
        <Stat id="plays" label="Reproducciones" value={s.total_plays} />
        <Stat id="tracks" label="Canciones" value={s.tracks} />
      </div>
      <p data-testid="admin-users-summary" className="font-mono text-xs text-slate-500">
        {s.users_by_role.artist} artistas · {s.users_by_role.curator} curadores · {s.users_by_role.admin} admins · {s.channels} canales · {s.media} medios · {s.submissions_done} envíos respondidos
      </p>
    </div>
  );
}

const blank = { slug: "", name: "", description: "", image: "" };

function ChannelRow({ c, reload, index, total, onMove, drag }) {
  const api = useApi();
  const [v, setV] = useState({ slug: c.slug, name: c.name, description: c.description, image: c.image });
  const save = async () => { try { await api("put", `/admin/channels/${c.id}`, v); toast.success("Canal guardado"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async () => { if (!window.confirm("¿Eliminar canal?")) return; await api("delete", `/admin/channels/${c.id}`); reload(); };
  return (
    <div data-testid={`admin-channel-${c.slug}`} {...drag} className={`grid items-center gap-2 border-b border-white/5 py-4 md:grid-cols-[auto_1fr_1fr_2fr_2fr_auto] ${drag.className}`}>
      <div className="flex items-center gap-1 text-slate-500">
        <GripVertical className="h-4 w-4 cursor-grab" />
        <button data-testid={`admin-channel-up-${c.slug}`} disabled={index === 0} onClick={() => onMove(index, index - 1)} className="p-1 hover:text-white disabled:opacity-20" aria-label="Subir"><ArrowUp className="h-3.5 w-3.5" /></button>
        <button data-testid={`admin-channel-down-${c.slug}`} disabled={index === total - 1} onClick={() => onMove(index, index + 1)} className="p-1 hover:text-white disabled:opacity-20" aria-label="Bajar"><ArrowDown className="h-3.5 w-3.5" /></button>
      </div>
      {["slug", "name", "description", "image"].map((k) => <Input key={k} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className={`h-9 ${fieldCls}`} />)}
      <div className="flex gap-1">
        <Button size="sm" data-testid={`admin-channel-save-${c.slug}`} onClick={save} className="rounded-full bg-white text-black hover:bg-slate-200">Guardar</Button>
        <button data-testid={`admin-channel-delete-${c.slug}`} onClick={del} className="p-2 text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function ChannelsAdmin() {
  const api = useApi();
  const [list, setList] = useState([]);
  const [n, setN] = useState(blank);
  const reload = () => api("get", "/channels").then(setList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);
  const [dragFrom, setDragFrom] = useState(null);
  const move = async (from, to) => {
    if (to < 0 || to >= list.length || from === to) return;
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setList(next);
    try { await api("put", "/admin/channels/order", { ids: next.map((c) => c.id) }); toast.success("Orden guardado"); } catch (e) { toast.error(errMsg(e)); reload(); }
  };
  const dragProps = (i) => ({
    draggable: true,
    onDragStart: () => setDragFrom(i),
    onDragOver: (e) => e.preventDefault(),
    onDrop: () => { move(dragFrom, i); setDragFrom(null); },
    className: dragFrom === i ? "opacity-40" : "",
  });
  const create = async () => { try { await api("post", "/admin/channels", n); setN(blank); toast.success("Canal creado"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-xl border border-white/10 bg-[#12121A] p-6">
      <p className="font-mono text-[11px] text-slate-500">ARRASTRA PARA ORDENAR · IDENTIFICADOR · NOMBRE · DESCRIPCIÓN · IMAGEN (URL)</p>
      {list.map((c, i) => <ChannelRow key={c.id} c={c} reload={reload} index={i} total={list.length} onMove={move} drag={dragProps(i)} />)}
      <div className="grid gap-2 pt-4 md:grid-cols-[1fr_1fr_2fr_2fr_auto]">
        {["slug", "name", "description", "image"].map((k) => <Input key={k} data-testid={`admin-new-channel-${k}`} placeholder={k === "slug" ? "ej: jazz" : k} value={n[k]} onChange={(e) => setN({ ...n, [k]: e.target.value })} className={`h-9 ${fieldCls}`} />)}
        <Button size="sm" data-testid="admin-new-channel-btn" onClick={create} className="rounded-full bg-[#FF4500] text-white hover:bg-[#ff5a1f]">Crear</Button>
      </div>
    </div>
  );
}

const ROLE_LABELS = { admin: "Administrador", curator: "Curador / Medio", artist: "Artista" };

export function UsersAdmin() {
  const api = useApi();
  const { me } = useMe();
  const [data, setData] = useState(null);
  const reload = () => api("get", "/users").then(setData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);
  const change = async (uid, role) => { try { await api("patch", `/users/${uid}/role`, { role }); toast.success("Rol actualizado"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!data) return null;
  return (
    <div data-testid="admin-users" className="rounded-xl border border-white/10 bg-[#12121A] p-2">
      {data.users.map((u) => (
        <div key={u.user_id} data-testid={`admin-user-${u.user_id}`} className="flex flex-wrap items-center gap-4 border-b border-white/5 p-4 last:border-0">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-white">{u.display_name || "(sin nombre)"} {u.user_id === me?.user_id && <span className="text-xs text-slate-500">(tú)</span>}</p>
            <p className="text-xs text-slate-500">{u.email} · {u.credits || 0} créditos · saldo {usd(u.earnings_usd)}</p>
          </div>
          <Select value={u.role} onValueChange={(r) => change(u.user_id, r)}>
            <SelectTrigger data-testid={`admin-user-role-${u.user_id}`} className={`h-9 w-48 ${fieldCls}`}><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#181824] text-slate-200">{data.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

export function MediaAdmin() {
  const api = useApi();
  const [list, setList] = useState([]);
  const reload = () => api("get", "/admin/media").then(setList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);
  const toggle = async (m) => { await api("patch", `/admin/media/${m.id}`, { hidden: !m.hidden }); reload(); };
  const verify = async (m) => { await api("patch", `/admin/media/${m.id}`, { verified: !m.verified }); toast.success(m.verified ? "Verificación quitada" : "Medio verificado"); reload(); };
  return (
    <div data-testid="admin-media" className="rounded-xl border border-white/10 bg-[#12121A] p-2">
      {list.map((m) => (
        <div key={m.id} className="flex items-center gap-4 border-b border-white/5 p-4 last:border-0">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-white">{m.name}</p>
            <p className="text-xs text-slate-500">{m.country} · {m.format} · {m.cost_credits} créditos · {m.owner_id ? "con curador" : "medio de ejemplo"}</p>
          </div>
          <span className="text-xs text-slate-400">Verificado</span>
          <Switch data-testid={`admin-media-verify-${m.id}`} checked={!!m.verified} onCheckedChange={() => verify(m)} />
          <span className="text-xs text-slate-400">{m.hidden ? "Oculto" : "Visible"}</span>
          <Switch data-testid={`admin-media-toggle-${m.id}`} checked={!m.hidden} onCheckedChange={() => toggle(m)} />
        </div>
      ))}
    </div>
  );
}

export function AdminUpload({ onDone }) {
  const api = useApi();
  const channels = useChannels();
  const [v, setV] = useState({ title: "", artist_name: "", channel_slug: "", lyrics: "" });
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const [progress, setProgress] = useState("");
  const audioRef = useRef(null);
  const coverRef = useRef(null);
  const submit = async (e) => {
    e.preventDefault();
    const files = [...audioRef.current.files];
    if (!files.length || !v.channel_slug) { toast.error("Elige el canal y al menos un archivo de audio"); return; }
    if (files.length === 1 && !v.title.trim()) { toast.error("Escribe el título de la canción"); return; }
    setBusy(true);
    let ok = 0;
    for (const [i, audio] of files.entries()) {
      setProgress(`Subiendo ${i + 1} de ${files.length}...`);
      const fd = new FormData();
      const title = files.length === 1 ? v.title : audio.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();
      fd.append("title", title); fd.append("artist_name", v.artist_name); fd.append("channel_slug", v.channel_slug); fd.append("lyrics", files.length === 1 ? v.lyrics : "");
      fd.append("audio", audio);
      if (coverRef.current.files[0]) fd.append("cover", coverRef.current.files[0]);
      try { await api("post", "/admin/tracks", fd); ok += 1; } catch (err) { toast.error(`${audio.name}: ${errMsg(err)}`); }
    }
    if (ok) toast.success(ok === 1 ? "Canción subida: ya suena en la radio" : `${ok} canciones subidas: ya suenan en la radio`);
    setV({ title: "", artist_name: v.artist_name, channel_slug: v.channel_slug, lyrics: "" });
    e.target.reset(); setCount(0); setProgress(""); setBusy(false);
    onDone?.();
  };
  const multi = count > 1;
  return (
    <form onSubmit={submit} data-testid="admin-upload-form" className="rounded-xl border border-white/10 bg-[#12121A] p-6">
      <p className="font-heading text-lg font-bold text-white">Subir música a la radio</p>
      <p className="text-xs text-slate-500">Las canciones que subes aquí suenan al instante, sin aprobación. Puedes elegir varios archivos a la vez: el título se toma del nombre de cada archivo.</p>
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        <Input data-testid="admin-upload-title" disabled={multi} placeholder={multi ? `${count} archivos: título del nombre de archivo` : "Título"} value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} className={fieldCls} />
        <Input data-testid="admin-upload-artist" required placeholder="Artista" value={v.artist_name} onChange={(e) => setV({ ...v, artist_name: e.target.value })} className={fieldCls} />
        <Select value={v.channel_slug} onValueChange={(s) => setV({ ...v, channel_slug: s })}>
          <SelectTrigger data-testid="admin-upload-channel" className={fieldCls}><SelectValue placeholder="Canal" /></SelectTrigger>
          <SelectContent className="border-white/10 bg-[#181824] text-slate-200">{channels?.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea data-testid="admin-upload-lyrics" disabled={multi} placeholder={multi ? "La letra se agrega de a una canción" : "Letra (opcional)"} value={v.lyrics} onChange={(e) => setV({ ...v, lyrics: e.target.value })} className={`${fieldCls} md:col-span-3`} />
        <label className="flex items-center gap-2 text-sm text-slate-400">Audio (MP3/WAV, máx. 25 MB c/u)
          <input data-testid="admin-upload-audio" ref={audioRef} type="file" multiple onChange={(e) => setCount(e.target.files.length)} accept="audio/mpeg,audio/wav,audio/x-wav,.mp3,.wav" className="text-xs file:mr-2 file:rounded-full file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-white" />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-400">Portada (opcional, para todas)
          <input data-testid="admin-upload-cover" ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp" className="text-xs file:mr-2 file:rounded-full file:border-0 file:bg-white/10 file:px-3 file:py-1 file:text-white" />
        </label>
      </div>
      <Button data-testid="admin-upload-submit" disabled={busy} className="mt-5 rounded-full bg-[#FF4500] text-white hover:bg-[#ff5a1f]"><Upload className="mr-1 h-4 w-4" />{busy ? progress || "Subiendo..." : multi ? `Subir ${count} canciones` : "Subir a la radio"}</Button>
    </form>
  );
}

export function TracksAdmin() {
  const api = useApi();
  const channels = useChannels();
  const [list, setList] = useState([]);
  const reload = () => api("get", "/admin/tracks").then(setList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);
  const move = async (t, slug) => { try { await api("patch", `/admin/tracks/${t.id}`, { channel_slug: slug }); toast.success("Canción movida"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (t) => { if (!window.confirm(`¿Quitar "${t.title}"?`)) return; try { await api("delete", `/admin/tracks/${t.id}`); toast.success("Canción quitada"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="admin-tracks" className="rounded-xl border border-white/10 bg-[#12121A] p-2">
      {list.map((t) => (
        <div key={t.id} className="flex items-center gap-4 border-b border-white/5 p-3 last:border-0">
          <MiniPlay track={t} testId={`admin-track-play-${t.id}`} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-white">{t.title} <span className="font-normal text-slate-500">· {t.artist_name}</span></p>
            <p className="font-mono text-[11px] text-slate-500">{t.channel_slug || "sin canal"} · {t.radio_status} · {t.plays} repr. {t.demo && "· EJEMPLO"} {t.featured && "· DESTACADA"}</p>
          </div>
          <Select value={t.channel_slug || ""} onValueChange={(s) => move(t, s)}>
            <SelectTrigger data-testid={`admin-track-channel-${t.id}`} className={`h-9 w-40 ${fieldCls}`}><SelectValue placeholder="Poner en canal" /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#181824] text-slate-200">{channels?.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <button data-testid={`admin-track-delete-${t.id}`} onClick={() => del(t)} className="p-2 text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}
